import { cn } from '@goaltree/ui/lib/utils'
import { ImagePlus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

function imageIn(items: DataTransfer | null): File | null {
  for (const f of Array.from(items?.files ?? [])) if (f.type.startsWith('image/')) return f
  return null
}

const carriesFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files')

// Paste (Cmd/Ctrl+V) or drop an image anywhere while `enabled`; `dragging` is true
// while a file is held over the window.
export function useImagePasteDrop(onImage: (file: File) => void, enabled: boolean) {
  const [dragging, setDragging] = useState(false)
  const onImageRef = useRef(onImage)
  onImageRef.current = onImage

  useEffect(() => {
    if (!enabled) return
    let depth = 0
    const onPaste = (e: ClipboardEvent) => {
      const file = imageIn(e.clipboardData)
      if (!file) return
      e.preventDefault()
      onImageRef.current(file)
    }
    const onEnter = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      depth++
      setDragging(true)
    }
    const onLeave = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      if (--depth <= 0) {
        depth = 0
        setDragging(false)
      }
    }
    const onOver = (e: DragEvent) => {
      if (carriesFiles(e)) e.preventDefault()
    }
    const onDrop = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth = 0
      setDragging(false)
      const file = imageIn(e.dataTransfer)
      if (file) onImageRef.current(file)
    }
    window.addEventListener('paste', onPaste)
    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('dragover', onOver)
    window.addEventListener('drop', onDrop)
    return () => {
      setDragging(false)
      window.removeEventListener('paste', onPaste)
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('drop', onDrop)
    }
  }, [enabled])

  return dragging
}

export function ImagePickButton({
  onImage,
  className,
  iconOnly = false,
  label,
  hint,
  onLocked,
}: {
  onImage: (file: File) => void
  className?: string
  iconOnly?: boolean
  label?: string
  hint?: string
  // Shown instead of the file chooser (e.g. the upgrade prompt for Free users).
  onLocked?: () => void
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) onImage(file)
        }}
      />
      <button
        type="button"
        onClick={() => (onLocked ? onLocked() : inputRef.current?.click())}
        aria-label={label ?? t('voice.fromImage')}
        title={hint ?? t('voice.fromImageHint')}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
          className,
        )}
      >
        <ImagePlus className="h-4 w-4" />
        {!iconOnly && (label ?? t('voice.fromImage'))}
      </button>
    </>
  )
}

export function ImageDropHint({ className }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex min-h-40 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 px-6 text-center',
        className,
      )}
    >
      <ImagePlus className="h-7 w-7 text-primary-text" />
      <p className="text-sm font-semibold text-foreground">{t('voice.dropImage')}</p>
      <p className="text-xs text-muted-foreground">{t('voice.dropImageHint')}</p>
    </div>
  )
}
